import { useTranslation } from '@/hooks/use-translation';
import type { components, paths } from '@/services/api-allo/api-allo-v1';
import { $apiAllo } from '@/services/api-allo/client';
import { type InfiniteData, type QueryKey, useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';
import { toast } from 'sonner';

type Contact = components['schemas']['Contact'];
type ContactsResponse =
  paths['/v1/contact']['get']['responses']['200']['content']['application/json'];
type UpdateContactBody =
  paths['/v1/contact/{contact_id}']['patch']['requestBody']['content']['application/json'];

// Filter query response type (used by useServerFilter)
type FilterResponse = {
  data: Contact[];
  metadata?: {
    pagination?: { current_page?: number; total_pages?: number };
  };
};

// Optimistic data for fields that need different shapes for UI vs API
type OptimisticData = {
  company?: components['schemas']['CompanyResponse'] | null;
};

interface MutationContext {
  previousData: [QueryKey, InfiniteData<ContactsResponse> | undefined][];
  previousFilterData: [QueryKey, InfiniteData<FilterResponse> | undefined][];
}

type ContactCacheTransform = (contact: Contact) => Contact;

/**
 * Builds a `setQueriesData` updater that applies `transform` to every contact
 * held in a `['get', '/v1/contact']` cache entry — handling BOTH shapes that
 * share this key:
 *   - infinite list `{ pages: [{ data: Contact[] }], pageParams }` (contacts view)
 *   - plain lookup `{ data: Contact[] }` (the `filter[number]` queries behind the
 *     call bar, dialer and Power Dialer queue rows)
 *
 * The plain shape was previously skipped (the old updater bailed when `pages`
 * was absent), so those by-number surfaces kept showing stale contact details
 * after an edit.
 */
export const buildContactCacheUpdater =
  (transform: ContactCacheTransform) =>
  (oldData: unknown): unknown => {
    if (!oldData || typeof oldData !== 'object') {
      return oldData;
    }
    if ('pages' in oldData) {
      const infinite = oldData as InfiniteData<ContactsResponse>;
      if (!infinite.pages?.length) {
        return oldData;
      }
      return {
        ...infinite,
        pages: infinite.pages.map((page) => ({
          ...page,
          data: page.data?.map(transform),
        })),
      };
    }
    if ('data' in oldData) {
      const plain = oldData as ContactsResponse;
      if (!Array.isArray(plain.data)) {
        return oldData;
      }
      return { ...plain, data: plain.data.map(transform) };
    }
    return oldData;
  };

export function useUpdateContact() {
  const { t } = useTranslation('desktop_ui');
  const queryClient = useQueryClient();

  // Store optimistic data for the current mutation (company object, etc.)
  const optimisticDataRef = useRef<OptimisticData | undefined>(undefined);

  const mutation = $apiAllo.useMutation('patch', '/v1/contact/{contact_id}', {
    onMutate: async (variables): Promise<MutationContext> => {
      // Cancel any outgoing refetches for both regular and filter queries
      await queryClient.cancelQueries({ queryKey: ['get', '/v1/contact'] });
      await queryClient.cancelQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === 'contacts' &&
          query.queryKey[1] === 'filter',
      });

      // Snapshot previous value for regular contacts query
      const previousData = queryClient.getQueriesData<InfiniteData<ContactsResponse>>({
        queryKey: ['get', '/v1/contact'],
      });

      // Snapshot previous value for filter queries
      const previousFilterData = queryClient.getQueriesData<InfiniteData<FilterResponse>>({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === 'contacts' &&
          query.queryKey[1] === 'filter',
      });

      // Get the optimistic data stored before mutation
      const optimistic = optimisticDataRef.current;
      optimisticDataRef.current = undefined;

      const {
        numbers,
        name,
        last_name,
        emails,
        website,
        job_title,
        addresses,
        company,
        custom_properties,
      } = variables.body;

      // Helper function to update a contact in a page
      const updateContactInPage = (contact: Contact): Contact => {
        if (contact.id !== variables.params.path.contact_id) {
          return contact;
        }
        return {
          ...contact,
          ...(numbers && { numbers }),
          ...(name !== undefined && { name }),
          ...(last_name !== undefined && { last_name }),
          ...(emails !== undefined && { emails }),
          ...(website !== undefined && { website }),
          ...(job_title !== undefined && { job_title }),
          ...(addresses !== undefined && { addresses }),
          // Use optimistic company object if provided, otherwise use the ID from body
          ...(company !== undefined && {
            company: optimistic?.company !== undefined ? optimistic.company : company,
          }),
          // Merge custom_properties for CRM attribute updates
          ...(custom_properties && {
            custom_properties: {
              ...((contact as Record<string, unknown>).custom_properties as
                | Record<string, unknown>
                | undefined),
              ...custom_properties,
            },
          }),
        } as Contact;
      };

      // Optimistically update the regular contacts cache — both the infinite
      // list (contacts view) and the plain `filter[number]` lookups that feed
      // the call bar, dialer and Power Dialer queue rows.
      queryClient.setQueriesData(
        { queryKey: ['get', '/v1/contact'] },
        buildContactCacheUpdater(updateContactInPage),
      );

      // Optimistically update the filter queries cache
      queryClient.setQueriesData<InfiniteData<FilterResponse>>(
        {
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            query.queryKey[0] === 'contacts' &&
            query.queryKey[1] === 'filter',
        },
        (oldData) => {
          if (!oldData?.pages?.length) {
            return oldData;
          }

          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              data: page.data?.map(updateContactInPage),
            })),
          };
        },
      );

      return { previousData, previousFilterData };
    },
    onSuccess: (response) => {
      const updatedContact = response.data;

      // Update regular contacts cache with actual server response — both the
      // infinite list and the plain `filter[number]` lookups.
      queryClient.setQueriesData(
        { queryKey: ['get', '/v1/contact'] },
        buildContactCacheUpdater((contact) =>
          contact.id === updatedContact?.id
            ? { ...updatedContact, external_data: contact.external_data }
            : contact,
        ),
      );

      // Update filter queries cache with actual server response
      queryClient.setQueriesData<InfiniteData<FilterResponse>>(
        {
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            query.queryKey[0] === 'contacts' &&
            query.queryKey[1] === 'filter',
        },
        (oldData) => {
          if (!oldData?.pages?.length) {
            return oldData;
          }

          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              data: page.data?.map((contact) =>
                contact.id === updatedContact?.id
                  ? ({ ...updatedContact, external_data: contact.external_data } as Contact)
                  : contact,
              ),
            })),
          };
        },
      );

      // Invalidate single contact query
      queryClient.invalidateQueries({
        queryKey: ['get', '/v1/contact/{contact_id}'],
      });
      queryClient.invalidateQueries({
        queryKey: ['get', '/v2/conversations'],
      });

      // Refresh conversations to reflect updated contact info
      queryClient.invalidateQueries({
        queryKey: ['get', '/v2/conversations'],
      });

      toast.success(t('components.contacts.form.updateContactSuccess'));
    },
    onError: (_error, _variables, context) => {
      // Rollback to previous data on error
      const ctx = context as MutationContext | undefined;
      if (ctx?.previousData) {
        for (const [queryKey, data] of ctx.previousData) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      // Rollback filter query data
      if (ctx?.previousFilterData) {
        for (const [queryKey, data] of ctx.previousFilterData) {
          queryClient.setQueryData(queryKey, data);
        }
      }

      toast.error(t('components.contacts.form.updateContactError'));
    },
  });

  const mutationRef = useRef(mutation);
  mutationRef.current = mutation;

  const updateContact = useCallback(
    (contactId: string, data: UpdateContactBody, optimisticData?: OptimisticData) => {
      // Store optimistic data before mutation so onMutate can access it
      optimisticDataRef.current = optimisticData;
      mutationRef.current.mutate({
        params: { path: { contact_id: contactId } },
        body: data,
      });
    },
    [],
  );

  return {
    updateContact,
    isUpdating: mutation.isPending,
  };
}
